import * as THREE from 'three'
import Config from '@glass-designer/config'
import MathsUtils from '@/utils/mathsUtils'

import GlassDesignerTypes from '@glass-designer/types'


// Base tool
// Used by all the tools in the editor to inherit from
export default class BaseTool {
  constructor(editor) {
    this.type = ''
    this.editor = editor
    this.sceneObject = new THREE.Object3D()
    this.touchUnproject = new THREE.Vector3()
    this.children = []

    this.panning = {
      active: false,
      posX: 0,
      posY: 0
    }
    this.dragZoom = {
      active: false,
      origPosX: 0,
      origPosY: 0,
      posX: 0,
      posY: 0
    }

    // Touch handling
    this.touchMode = GlassDesignerTypes.TouchCameraModes.touchPan
    this.touchCentre = new THREE.Vector2()
    this.touchZoomDistanceStart = 0.0
    this.touchZoomDistanceEnd = 0.0
  }

  // Dispose the tool
  dispose() {
  }

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    this.children.forEach(childObj => {
      childObj.onBeforeRender()
    })    
  }

  addChildObject(obj) {
    if(this.sceneObject)
      this.sceneObject.add(obj.getSceneObject())
      
    this.children.push(obj)
  }
  removeChildObject(obj) {
    this.children = this.children.filter(c => c.id != obj.id)
  }

  getSceneObject() {
    return this.sceneObject
  }

  // Tool Active
  // Called when the tool is active
  onToolActive(options = {}) {
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
  }

  // Called to close & finish this tool & reset editor tool
  closeTool() {
    this.editor.cancelActiveTool()
  }

  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData) {
    var panButton = Config.Mouse.buttonPan

    if(ev.buttons & panButton && ev.shiftKey) {
      // Shift-Right Click to drag zoom
      this.dragZoom.active = true
      this.dragZoom.origPosX = mouseData.position.x
      this.dragZoom.origPosY = mouseData.position.y
      this.dragZoom.posX = ev.offsetX
      this.dragZoom.posY = ev.offsetY

    } else if(ev.buttons & panButton) {
      // Pan
      this.panning.active = true
      this.panning.posX = ev.offsetX
      this.panning.posY = ev.offsetY
    }

    return false
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    if(this.panning.active) {
      let dirX = this.panning.posX - ev.offsetX
      let dirY = this.panning.posY - ev.offsetY
      this.panning.posX = ev.offsetX
      this.panning.posY = ev.offsetY

      // Move the camera
      this.editor.camera.position.x += dirX / this.editor.camera.zoom
      this.editor.camera.position.y += dirY / this.editor.camera.zoom

      // Clamp camera to world limits
      this.clampCameraPosition()

      this.editor.onCameraUpdated()
    }
    if(this.dragZoom.active) {
      let dirY = this.dragZoom.posY - ev.offsetY
      this.dragZoom.posY = ev.offsetY

      this.editor.zoomStep += dirY / Config.Mouse.dragZoomFactor
      this.editor.zoomStep = Math.max(this.editor.zoomStep, 0)
      this.editor.zoomStep = Math.min(this.editor.zoomStep, Config.Zooming.zoomStepCount)
  
      let logZoom = Math.log(Config.Zooming.minZoom) + (Math.log(Config.Zooming.maxZoom)- Math.log(Config.Zooming.minZoom))*this.editor.zoomStep/(Config.Zooming.zoomStepCount-1)
  
      // Move camera towards mouse
      let moveMultiplier = ((Math.exp(logZoom) - this.editor.zoomScale) / this.editor.zoomScale)
      this.editor.camera.position.x += (this.dragZoom.origPosX - this.editor.camera.position.x) * moveMultiplier
      this.editor.camera.position.y += (this.dragZoom.origPosY - this.editor.camera.position.y) * moveMultiplier
      
      this.editor.zoomScale = Math.exp(logZoom)
  
      this.editor.camera.zoom = this.editor.zoomScale
      this.editor.camera.updateProjectionMatrix()
  
      this.clampCameraPosition()
      this.editor.onCameraUpdated()
    }

    return false
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    this.panning.active = false
    this.dragZoom.active = false

    return false
  }

  // onMouseWheelEvent
  onMouseWheelEvent(ev, mouseData) {
    // Logarithmic zooming
    this.editor.zoomStep += ev.wheelDeltaY / Config.Mouse.wheelZoomFactor
    this.editor.zoomStep = Math.max(this.editor.zoomStep, 0)
    this.editor.zoomStep = Math.min(this.editor.zoomStep, Config.Zooming.zoomStepCount)

    let logZoom = Math.log(Config.Zooming.minZoom) + (Math.log(Config.Zooming.maxZoom)- Math.log(Config.Zooming.minZoom))*this.editor.zoomStep/(Config.Zooming.zoomStepCount-1)

    // Move camera towards mouse
    let moveMultiplier = ((Math.exp(logZoom) - this.editor.zoomScale) / this.editor.zoomScale)
    this.editor.camera.position.x += (mouseData.position.x - this.editor.camera.position.x) * moveMultiplier
    this.editor.camera.position.y += (mouseData.position.y - this.editor.camera.position.y) * moveMultiplier
    
    this.editor.zoomScale = Math.exp(logZoom)

    this.editor.camera.zoom = this.editor.zoomScale
    this.editor.camera.updateProjectionMatrix()

    this.clampCameraPosition()

    return false
  }

  onTouchStartEvent(ev, touchData) {
    switch(ev.touches.length) {
      case 1: {
        this.touchMode = GlassDesignerTypes.TouchCameraModes.touchPan
        this.touchCentre.x = ev.touches[0].pageX
        this.touchCentre.y = ev.touches[0].pageY
        break
      }
      case 2: {
        this.touchMode = GlassDesignerTypes.TouchCameraModes.touchPanZoom
        let distX = ev.touches[1].pageX - ev.touches[0].pageX
        let distY = ev.touches[1].pageY - ev.touches[0].pageY
        this.touchZoomDistanceStart = Math.sqrt(distX*distX + distY*distY)
        this.touchZoomDistanceEnd = this.touchZoomDistanceStart

        this.touchCentre.x = (ev.touches[0].pageX + ev.touches[1].pageX) / 2
        this.touchCentre.y = (ev.touches[0].pageY + ev.touches[1].pageY) / 2
        break
      }
      default: 
        this.touchMode = GlassDesignerTypes.TouchCameraModes.touchNone
    }

    return false
  }

  onTouchEndEvent(ev, touchData) {
    this.touchMode = GlassDesignerTypes.TouchCameraModes.touchNone

    this.touchZoomDistanceStart = 0
    this.touchZoomDistanceEnd = 0

    return false
  }
  onTouchCancelEvent(ev, touchData) {
    this.touchMode = GlassDesignerTypes.TouchCameraModes.touchNone

    return false
  }
  onTouchMoveEvent(ev, touchData) {
    var dirX, dirY

    if(this.touchMode == GlassDesignerTypes.TouchCameraModes.touchNone)
      return this.onTouchStartEvent(ev, touchData)

    switch(ev.touches.length) {
      case 1: {
        dirX = this.touchCentre.x - ev.touches[0].pageX
        dirY = this.touchCentre.y - ev.touches[0].pageY

        // Move the camera
        this.editor.camera.position.x += dirX / this.editor.camera.zoom
        this.editor.camera.position.y += dirY / this.editor.camera.zoom

        this.touchCentre.x = ev.touches[0].pageX
        this.touchCentre.y = ev.touches[0].pageY
        break
      }

      case 2: {
        let centreX = (ev.touches[0].pageX + ev.touches[1].pageX) / 2
        let centreY = (ev.touches[0].pageY + ev.touches[1].pageY) / 2
        dirX = this.touchCentre.x - centreX
        dirY = this.touchCentre.y - centreY

        let distX = ev.touches[1].pageX - ev.touches[0].pageX
        let distY = ev.touches[1].pageY - ev.touches[0].pageY

        this.touchZoomDistanceEnd = Math.sqrt(distX*distX + distY*distY)
        let zoomFactor = this.touchZoomDistanceStart / this.touchZoomDistanceEnd
        this.touchZoomDistanceStart = this.touchZoomDistanceEnd

        let prevZoomScale = this.editor.zoomScale

        if(zoomFactor > 0) {
          this.editor.zoomScale = MathsUtils.clampValue(this.editor.zoomScale / zoomFactor, Config.Zooming.minZoom, Config.Zooming.maxZoom)
          this.editor.camera.zoom = this.editor.zoomScale
          this.editor.camera.updateProjectionMatrix()
        }

        // Move the camera
        this.editor.camera.position.x += dirX / this.editor.camera.zoom
        this.editor.camera.position.y += dirY / this.editor.camera.zoom

        let clientRect = this.editor.component.$el.getBoundingClientRect()

        // Need to move the camera towards the centre point of the pin/zoom
        let elementCentreX = centreX - clientRect.left
        let elementCentreY = centreY - clientRect.top

        let adjustX = (elementCentreX / this.editor.settings.viewportWidth) * 2 - 1
        let adjustY = -(elementCentreY / this.editor.settings.viewportHeight) * 2 + 1

        this.touchUnproject.set(adjustX, adjustY, ( this.editor.camera.near + this.editor.camera.far ) / ( this.editor.camera.near - this.editor.camera.far ))
        this.touchUnproject.unproject(this.editor.camera)

        let moveMultiplier = ((this.editor.zoomScale - prevZoomScale) / prevZoomScale)

        this.editor.camera.position.x += (this.touchUnproject.x - this.editor.camera.position.x) * moveMultiplier
        this.editor.camera.position.y += (this.touchUnproject.y - this.editor.camera.position.y) * moveMultiplier
        
        this.touchCentre.x = centreX
        this.touchCentre.y = centreY
        break;
      }

      default:
        this.touchMode = GlassDesignerTypes.TouchCameraModes.touchNone
    }

    this.clampCameraPosition()
    this.editor.onCameraUpdated()

    return false
  }

  onKeyDownEvent(ev) {
  }


  clampCameraPosition() {
    let maxPosX = (this.editor.settings.maxWorldWidth / 2) - (this.editor.settings.viewportWidth / 2) / this.editor.camera.zoom
    let maxPosY = (this.editor.settings.maxWorldHeight / 2) - (this.editor.settings.viewportHeight / 2) / this.editor.camera.zoom
    this.editor.camera.position.x = Math.min(this.editor.camera.position.x, maxPosX)
    this.editor.camera.position.x = Math.max(this.editor.camera.position.x, -maxPosX)
    this.editor.camera.position.y = Math.min(this.editor.camera.position.y, maxPosY)
    this.editor.camera.position.y = Math.max(this.editor.camera.position.y, -maxPosY)
  }

  // A dimension used while editing in this tool was clicked
  editingDimensionClicked(dimension) {
  }

  // A library item was dragged into the editor while this tool was active
  libraryItemDraggedToEditor(itemInfo) {
    
  }

  onReceiveObjectEvent(object, eventType, data) {
  }

  // Get if the editing mode is available
  isEditingAvailable() {
    return this.editor.isEditingAvailable()
  }

  /**
   * Find the closest object to the mouse / touch event
   * Returns a BaseObject under the event
   * Returns null if no object is at the mouse / touch location
   */
  _findClosestObjectToRay(ray, radius) {
    var objList = this.editor.objectList.filter(obj => obj.listenMouseEvents && obj.isVisible() && obj.isEnabled())

    return this.editor.findClosestObjectToRay(objList, ray, radius)
  }


  /**
   * Calculate the horizontal position of the popover based on current selection
   * Used to move the popover out of the way if covering the selection
   */
  _calculatePopoverHorizontalPosition(extraBounds = null) {
    var viewingWorldBounds = this.editor.calculateWorldViewBounds()
    let selectionBounds = this.editor.calculateSelectedObject2DBounds()

    if(extraBounds) {
      selectionBounds.expandByPoint(extraBounds.min)
      selectionBounds.expandByPoint(extraBounds.max)
    }

    var renderScale = 1.0 / this.editor.camera.zoom
    let popoverHeight = 300 * renderScale

    if(selectionBounds.max.y > viewingWorldBounds.max.y - popoverHeight) {
      let viewingCenter = viewingWorldBounds.getCenter(new THREE.Vector2())
      let selectionCenter = selectionBounds.getCenter(new THREE.Vector2())
      
      return selectionCenter.x > viewingCenter.x ? GlassDesignerTypes.PopoverPositions.left : GlassDesignerTypes.PopoverPositions.right
    }
    return GlassDesignerTypes.PopoverPositions.center
  }
}
